// src/components/Modals/ConsumableStockModal.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Slide,
  Stack,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  TextField,
  Typography,
  InputAdornment,
  Tabs,
  Tab,
  Skeleton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Tooltip,
} from "@mui/material";

import CloseIcon               from "@mui/icons-material/Close";
import AddCircleOutlineIcon    from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import InventoryIcon           from "@mui/icons-material/Inventory";
import ReceiptLongIcon         from "@mui/icons-material/ReceiptLong";
import HistoryIcon             from "@mui/icons-material/History";
import InfoOutlinedIcon        from "@mui/icons-material/InfoOutlined";
import ArrowForwardIcon        from "@mui/icons-material/ArrowForward";
import SearchIcon              from "@mui/icons-material/Search"; 

import { consumableSpareLogService } from "../../services/consumableSpareLogService";
import { assetService }              from "../../services/assetService";
import { ConsumableSpareLogModel }   from "../../models/ConsumableSpareLogModel";

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

const UOM_OPTIONS = ConsumableSpareLogModel.ALLOWED_UOM;

const TRANSACTION_TYPES = {
  Receipt: {
    label:       "Receipt (Stock In)",
    color:       "success",
    icon:        <AddCircleOutlineIcon />,
    description: "Record incoming stock — purchase, return, or transfer in.",
  },
  Issue: {
    label:       "Issue (Stock Out)",
    color:       "error",
    icon:        <RemoveCircleOutlineIcon />,
    description: "Record outgoing stock — issued to equipment or work order.",
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
};

const formatQty = (qty, uom) =>
  `${Number(qty || 0).toLocaleString()} ${uom || ""}`.trim();

// ─────────────────────────────────────────────────────────────────────────────
// SLIDE TRANSITION
// ─────────────────────────────────────────────────────────────────────────────

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ─────────────────────────────────────────────────────────────────────────────
// STYLED COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

const SectionCard = styled(Card)(() => ({
  borderRadius: 8,
  border: "1px solid #e2e8f0",
}));

const SectionTitle = styled(Typography)(() => ({
  fontSize: "0.9375rem",
  fontWeight: 700,
  color: "#1e293b",
  marginBottom: "1.25rem",
  paddingBottom: "0.75rem",
  borderBottom: "1px solid #e5e7eb",
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
}));

const FormRow = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: "1rem",
  marginBottom: "1rem",
  "&:last-child": { marginBottom: 0 },
  [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
}));

const FormGroup = styled(Box, {
  shouldForwardProp: (p) => p !== "hasError" && p !== "span",
})(({ hasError, span = 2 }) => ({
  gridColumn: `span ${span}`,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: "0.375rem",
  "& .MuiOutlinedInput-root": {
    background: "#fff",
    "& fieldset":             { borderColor: hasError ? "#dc2626" : "#d1d5db" },
    "&:hover fieldset":       { borderColor: hasError ? "#dc2626" : "#3b82f6" },
    "&.Mui-focused fieldset": {
      borderColor: hasError ? "#dc2626" : "#3b82f6",
      boxShadow: hasError
        ? "0 0 0 3px rgba(220,38,38,0.12)"
        : "0 0 0 3px rgba(59,130,246,0.12)",
    },
  },
}));

const FieldLabel = styled(Typography)(() => ({
  fontSize: "0.8125rem",
  fontWeight: 600,
  color: "#374151",
  textTransform: "uppercase",
  letterSpacing: "0.025em",
  display: "inline-flex",
  alignItems: "center",
  gap: "0.375rem",
}));

const RequiredMark = styled("span")(() => ({
  color: "#ef4444",
  marginLeft: "0.125rem",
}));

const StatBox = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "0.125rem",
}));

const StatLabel = styled(Typography)(() => ({
  fontSize: "0.6875rem",
  fontWeight: 600,
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
}));

const helperErrorSx = {
  color: "#dc2626", fontSize: "0.75rem",
  fontWeight: 500, margin: 0, marginTop: "4px",
};

const helperHintSx = {
  color: "#6b7280", fontSize: "0.75rem",
  margin: 0, marginTop: "4px",
};

const cancelBtnSx = {
  px: 3, py: 1, backgroundColor: "#f3f4f6", color: "#374151",
  border: "1px solid #d1d5db", borderRadius: "6px", fontWeight: 500,
  textTransform: "none",
  "&:hover": { backgroundColor: "#e5e7eb" },
  "&:disabled": { opacity: 0.6 },
};

// ─────────────────────────────────────────────────────────────────────────────
// STOCK HISTORY TABLE
// ─────────────────────────────────────────────────────────────────────────────

const StockHistoryTable = ({ logs, loading, openingStock = 0 }) => {
  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} variant="rounded" height={44} sx={{ mb: 1, borderRadius: 1 }} />
        ))}
      </Box>
    );
  }

  if (!logs.length) {
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <HistoryIcon sx={{ fontSize: "2.5rem", color: "#d1d5db", mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          No transactions recorded yet.
        </Typography>
      </Box>
    );
  }

  const chronological = [...logs].reverse();

  const netFromLogs = chronological.reduce((sum, log) => {
    const qty = Number(log.qty || 0);
    return log.transactionType === "Receipt" ? sum + qty : sum - qty;
  }, 0);

  let balance = openingStock - netFromLogs;

  const withBalance = chronological.map((log) => {
    const qty = Number(log.qty || 0);
    balance += log.transactionType === "Receipt" ? qty : -qty;
    return { ...log, balance: Math.max(balance, 0) };
  });

  const displayRows = [...withBalance].reverse();

  const HEADERS = [
    "Date & Time", "Type", "Qty", "Unit Price",
    "Balance", "Reference", "Equipment", "Remarks",
  ];

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{
        borderRadius: 2,
        maxHeight: 420,
        overflowY: "auto",
        "&::-webkit-scrollbar": { width: 6 },
        "&::-webkit-scrollbar-thumb": {
          bgcolor: "rgba(0,0,0,0.12)", borderRadius: 3,
        },
      }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {HEADERS.map((h) => (
              <TableCell
                key={h}
                sx={{
                  fontWeight: 700, bgcolor: "#f8fafc",
                  fontSize: "0.72rem", whiteSpace: "nowrap",
                  color: "#374151", letterSpacing: "0.025em",
                  textTransform: "uppercase",
                }}
              >
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {displayRows.map((log) => (
            <TableRow key={log.id} hover>
              <TableCell sx={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}>
                {formatDateTime(log.transactionDateTime || log.createdDate)}
              </TableCell>
              <TableCell>
                <Chip
                  label={log.transactionType}
                  size="small"
                  color={log.transactionType === "Receipt" ? "success" : "error"}
                  variant="outlined"
                  sx={{ fontWeight: 600, fontSize: "0.68rem" }}
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem" }}>
                <Typography
                  component="span"
                  sx={{
                    color: log.transactionType === "Receipt" ? "#059669" : "#dc2626",
                    fontWeight: 700,
                  }}
                >
                  {log.transactionType === "Receipt" ? "+" : "−"}
                  {formatQty(log.qty, log.uom)}
                </Typography>
              </TableCell>
              <TableCell sx={{ fontSize: "0.75rem" }}>
                {log.unitPrice ? `₹${Number(log.unitPrice).toLocaleString()}` : "—"}
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem", color: "#2563eb" }}>
                {Number(log.balance || 0).toLocaleString()} {log.uom || ""}
              </TableCell>
              <TableCell sx={{ fontSize: "0.75rem", color: "#6b7280" }}>
                {log.referenceNumber || "—"}
              </TableCell>
              <TableCell sx={{ fontSize: "0.75rem", color: "#6b7280" }}>
                {log.equipmentAssetName || "—"}
              </TableCell>
              <TableCell sx={{ fontSize: "0.75rem", color: "#6b7280", maxWidth: 140 }}>
                <Tooltip title={log.remarks || ""} arrow>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {log.remarks || "—"}
                  </Typography>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SEARCHABLE EQUIPMENT SELECT  ← NEW COMPONENT
// Renders a search box pinned at the top of the dropdown menu so the user
// can type to filter the asset list instead of scrolling through hundreds
// of entries. The search box is rendered as a non-selectable MenuItem so
// it stays inside the MUI Select popover without closing it on click.
// ─────────────────────────────────────────────────────────────────────────────

const SearchableEquipmentSelect = ({
  value,
  onChange,
  assets,
  loading,
  error,
}) => {
  // ── local search query — lives only inside this component ────────────────
  const [searchQuery, setSearchQuery] = useState("");

  // Reset search whenever the dropdown closes (value changes or component
  // re-renders with a new selection)
  const handleChange = (e) => {
    onChange(e);
    setSearchQuery("");
  };

  // Filter assets by assetName, assetId, assetType, or locationName
  const filteredAssets = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return assets;
    return assets.filter((a) =>
      [a.assetName, a.assetId, a.assetType, a.locationName, a.currentLocationName]
        .some((field) => field?.toLowerCase().includes(q))
    );
  }, [assets, searchQuery]);

  return (
    <FormControl fullWidth size="small" error={Boolean(error)}>
      <Select
        value={value}
        onChange={handleChange}
        disabled={loading}
        displayEmpty
        // Keep the menu open while the user types in the search box
        // by stopping keydown events from bubbling to the Select's
        // built-in keyboard navigation handler.
        onKeyDown={(e) => e.stopPropagation()}
        MenuProps={{
          // Prevent the menu from closing when the search TextField is clicked
          autoFocus: false,
          PaperProps: {
            sx: { maxHeight: 360 },
          },
        }}
        sx={{
          background: "#fff",
          "& fieldset": {
            borderColor: error ? "#dc2626" : "#d1d5db",
          },
        }}
        renderValue={(selected) => {
          if (!selected) {
            return (
              <Typography sx={{ opacity: 0.6, fontStyle: "italic", fontSize: "0.875rem" }}>
                {loading ? "Loading assets…" : "Select target equipment or asset"}
              </Typography>
            );
          }
          const found = assets.find((a) => a.id === selected);
          return found ? (
            <Typography variant="body2" fontWeight={600}>
              {found.assetName}
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {found.assetId}
              </Typography>
            </Typography>
          ) : selected;
        }}
      >
        {/* ── Pinned search box ── */}
        <MenuItem
          disableRipple
          // Prevent the MenuItem click from selecting an empty value
          onClickCapture={(e) => e.stopPropagation()}
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1,
            bgcolor: "#fff",
            borderBottom: "1px solid #e5e7eb",
            p: "8px 12px",
            "&:hover": { bgcolor: "#fff" },
            "&.Mui-focusVisible": { bgcolor: "#fff" },
          }}
        >
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Search by name, ID, type or location…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            // Stop the Select from intercepting key events while typing
            onKeyDown={(e) => e.stopPropagation()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: "1rem", color: "#9ca3af" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: "#f9fafb",
                "& fieldset": { borderColor: "#e5e7eb" },
                "&:hover fieldset": { borderColor: "#3b82f6" },
                "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
              },
            }}
          />
        </MenuItem>

        {/* ── Placeholder disabled option ── */}
        <MenuItem value="" disabled sx={{ display: "none" }} />

        {/* ── Filtered asset list ── */}
        {filteredAssets.length === 0 ? (
          <MenuItem disabled>
            <Typography
              sx={{ opacity: 0.6, fontStyle: "italic", fontSize: "0.875rem" }}
            >
              {loading ? "Loading assets…" : "No assets match your search."}
            </Typography>
          </MenuItem>
        ) : (
          filteredAssets.map((eq) => (
            <MenuItem key={eq.id} value={eq.id}>
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {eq.assetName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {eq.assetId} · {eq.assetType} ·{" "}
                  {eq.locationName || eq.currentLocationName || ""}
                </Typography>
              </Box>
            </MenuItem>
          ))
        )}
      </Select>

      {error && (
        <FormHelperText sx={helperErrorSx}>{error}</FormHelperText>
      )}
    </FormControl>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

const ConsumableStockModal = ({
  open,
  asset,
  currentUser,
  defaultTransactionType,
  onClose,
  onSuccess,
}) => {
  const [activeTab, setActiveTab] = useState(0);

  // Form state
  const [transactionType,      setTransactionType]      = useState(defaultTransactionType || "Receipt");
  const [qty,                  setQty]                  = useState("");
  const [uom,                  setUom]                  = useState(asset?.unitOfMeasurement || "Nos");
  const [unitPrice,            setUnitPrice]            = useState("");
  const [referenceNumber,      setReferenceNumber]      = useState("");
  const [remarks,              setRemarks]              = useState("");
  const [transactionDateTime,  setTransactionDateTime]  = useState(
    new Date().toISOString().slice(0, 16)
  );

  // Equipment (Issue only)
  const [equipmentAssets,  setEquipmentAssets]  = useState([]);
  const [equipmentAssetId, setEquipmentAssetId] = useState("");
  const [loadingEquipment, setLoadingEquipment] = useState(false);

  // History
  const [logs,        setLogs]        = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  // UI
  const [submitting,   setSubmitting]   = useState(false);
  const [error,        setError]        = useState("");
  const [fieldErrors,  setFieldErrors]  = useState({});
  const [loadingStock, setLoadingStock] = useState(false);

  const [currentStock, setCurrentStock] = useState(Number(asset?.quantity || 0));

  // ── Fetch authoritative stock from server ─────────────────────────────────
  const refreshStock = useCallback(async () => {
    if (!asset?.id || !currentUser) return;
    setLoadingStock(true);
    try {
      const stock = await consumableSpareLogService.getCurrentStock(
        asset.id, currentUser
      );
      setCurrentStock(stock);
    } catch (err) {
      console.warn("Could not refresh stock:", err.message);
      setCurrentStock(Number(asset?.quantity || 0));
    } finally {
      setLoadingStock(false);
    }
  }, [asset?.id, asset?.quantity, currentUser]);

  // ── Load logs ─────────────────────────────────────────────────────────────
  const loadLogs = useCallback(async () => {
    if (!asset?.id || !currentUser) return;
    setLoadingLogs(true);
    try {
      const data = await consumableSpareLogService.getLogsByAssetId(
        asset.id, currentUser, 200
      );
      setLogs(data);
    } catch (err) {
      console.warn("Could not load logs:", err.message);
      setLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  }, [asset?.id, currentUser]);

  // ── Reset on open + refresh stock & logs ──────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setTransactionType(defaultTransactionType || "Receipt");
    setQty("");
    setUom(asset?.unitOfMeasurement || "Nos");
    setUnitPrice("");
    setReferenceNumber("");
    setRemarks("");
    setTransactionDateTime(new Date().toISOString().slice(0, 16));
    setEquipmentAssetId("");
    setError("");
    setFieldErrors({});
    setActiveTab(0);
    refreshStock();
    loadLogs();
  }, [open, asset, defaultTransactionType, refreshStock, loadLogs]);

  // ── Auto-refresh stock + logs when user switches tabs ────────────────────
  useEffect(() => {
    if (!open) return;
    if (activeTab === 0) {
      refreshStock();
    } else if (activeTab === 1) {
      refreshStock();
      loadLogs();
    }
  }, [activeTab, open, refreshStock, loadLogs]);

  // ── Auto-refresh when window regains focus ────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const handleFocus = () => {
      refreshStock();
      if (activeTab === 1) loadLogs();
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [open, activeTab, refreshStock, loadLogs]);

  // ── Load equipment ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open || !currentUser) return;
    const load = async () => {
      setLoadingEquipment(true);
      try {
        const allAssets = await assetService.getAssets(currentUser, 500);
        setEquipmentAssets(
          allAssets.filter((a) => a.id !== asset?.id)
        );
      } catch (err) {
        console.warn("Could not load equipment assets:", err.message);
        setEquipmentAssets([]);
      } finally {
        setLoadingEquipment(false);
      }
    };
    load();
  }, [open, currentUser, asset?.id]);

  // ── Projected stock ───────────────────────────────────────────────────────
  const projectedStock = useMemo(() => {
    const qtyNum = Number(qty) || 0;
    if (!qtyNum) return currentStock;
    return transactionType === "Receipt"
      ? currentStock + qtyNum
      : Math.max(currentStock - qtyNum, 0);
  }, [currentStock, qty, transactionType]);

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = useCallback(() => {
    const errs   = {};
    const qtyNum = Number(qty);

    if (!qty || !Number.isFinite(qtyNum) || qtyNum <= 0) {
      errs.qty = "Quantity must be a valid number greater than zero.";
    } else if (transactionType === "Issue" && qtyNum > currentStock) {
      errs.qty = `Insufficient stock. Available: ${currentStock} ${uom}.`;
    }

    if (transactionType === "Issue" && !equipmentAssetId) {
      errs.equipmentAssetId = "Please select the target equipment.";
    }

    if (!transactionDateTime) {
      errs.transactionDateTime = "Transaction date & time is required.";
    }

    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }, [qty, transactionType, currentStock, uom, equipmentAssetId, transactionDateTime]);

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    setError("");
    if (!validate()) return;

    setSubmitting(true);
    try {
      const selectedEquipment = equipmentAssets.find((e) => e.id === equipmentAssetId);

      const result = await consumableSpareLogService.recordTransaction(currentUser, {
        assetId:             asset.id,
        assetName:           asset.assetName,
        assetType:           asset.assetType,
        transactionType,
        transactionDateTime: new Date(transactionDateTime).toISOString(),
        referenceNumber:     referenceNumber.trim(),
        remarks:             remarks.trim(),
        category:            asset.assetType === "Spare Part" ? "Spares" : "Consumables",
        unitPrice,
        qty,
        uom,
        equipmentAssetId:    equipmentAssetId             || "",
        equipmentAssetName:  selectedEquipment?.assetName || "",
      });

      setCurrentStock(result.stockAfter);
      await loadLogs();

      setQty("");
      setUnitPrice("");
      setReferenceNumber("");
      setRemarks("");
      setEquipmentAssetId("");
      setTransactionDateTime(new Date().toISOString().slice(0, 16));

      if (onSuccess) onSuccess(result);
      setActiveTab(1);
    } catch (err) {
      setError(err.message || "Transaction failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }, [
    validate, currentUser, asset, transactionType, transactionDateTime,
    referenceNumber, remarks, unitPrice, qty, uom,
    equipmentAssetId, equipmentAssets, loadLogs, onSuccess,
  ]);

  if (!asset) return null;

  const typeConfig     = TRANSACTION_TYPES[transactionType];
  const isReceipt      = transactionType === "Receipt";
  const accentColor    = isReceipt ? "#059669" : "#dc2626";
  const accentBg       = isReceipt ? "#f0fdf4" : "#fef2f2";
  const accentBorder   = isReceipt ? "#bbf7d0" : "#fecaca";
  const showQtyPreview = Number(qty) > 0;

  return (
    <Dialog
      fullScreen open={open}
      onClose={!submitting ? onClose : undefined}
      TransitionComponent={Transition}
      PaperProps={{
        sx: { display: "flex", flexDirection: "column", overflow: "hidden" },
      }}
    >
      {/* ── AppBar ──────────────────────────────────────────────────────── */}
      <AppBar sx={{ position: "relative", flexShrink: 0 }} elevation={1}>
        <Toolbar>
          <IconButton
            edge="start" color="inherit"
            onClick={onClose} disabled={submitting} aria-label="Close"
          >
            <CloseIcon />
          </IconButton>

          <Box sx={{ ml: 2, flex: 1, display: "flex", alignItems: "center", gap: 1.5 }}>
            <InventoryIcon sx={{ opacity: 0.9 }} />
            <Box>
              <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                Stock Transaction
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                {asset.assetName} —{" "}
                <span style={{ opacity: 0.75 }}>{asset.assetType}</span>
              </Typography>
            </Box>
          </Box>

          {/* Live stock badge */}
          <Box
            sx={{
              display: "flex", alignItems: "center", gap: 1,
              bgcolor: "rgba(255,255,255,0.15)", borderRadius: "8px",
              px: 2, py: 0.75, mr: 1,
            }}
          >
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Current Stock:
            </Typography>
            {loadingStock ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                {currentStock.toLocaleString()} {uom}
              </Typography>
            )}
            {showQtyPreview && (
              <>
                <ArrowForwardIcon sx={{ fontSize: "0.875rem", opacity: 0.7 }} />
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 800,
                    color: isReceipt
                      ? "#86efac"
                      : projectedStock === 0 ? "#fca5a5" : "#fde68a",
                  }}
                >
                  {projectedStock.toLocaleString()} {uom}
                </Typography>
              </>
            )}
          </Box>
        </Toolbar>

        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            px: 2,
            "& .MuiTab-root": {
              color: "rgba(255,255,255,0.7)",
              textTransform: "none",
              fontWeight: 600,
              minHeight: 44,
            },
            "& .Mui-selected":      { color: "#fff !important" },
            "& .MuiTabs-indicator": { backgroundColor: "#fff" },
          }}
        >
          <Tab
            label="New Transaction"
            icon={<ReceiptLongIcon fontSize="small" />}
            iconPosition="start"
          />
          <Tab
            label={`History (${logs.length})`}
            icon={<HistoryIcon fontSize="small" />}
            iconPosition="start"
          />
        </Tabs>
      </AppBar>

      {/* ── Body ────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex", flexDirection: "column",
          bgcolor: "#f1f5f9", flex: 1, minHeight: 0, overflow: "hidden",
        }}
      >
        <Box sx={{ flex: 1, overflowY: "auto", py: 4 }}>
          <Container maxWidth="lg">
            <Stack spacing={3}>

              {/* Error banner */}
              {error && (
                <Box
                  sx={{
                    backgroundColor: "#fee2e2", color: "#b91c1c",
                    p: "0.75rem 1rem", borderRadius: "6px",
                    fontSize: "0.875rem", borderLeft: "4px solid #dc2626",
                    display: "flex", alignItems: "center", gap: 1,
                  }}
                >
                  {error}
                </Box>
              )}

              {/* ══════════════════════════════════════════════════════════
                  TAB 0 — NEW TRANSACTION FORM
              ══════════════════════════════════════════════════════════ */}
              {activeTab === 0 && (
                <>
                  {/* Transaction Type selector */}
                  <SectionCard variant="outlined">
                    <CardContent sx={{ p: 3 }}>
                      <SectionTitle>
                        <ReceiptLongIcon sx={{ fontSize: "1.125rem", color: "#3b82f6" }} />
                        Transaction Type
                      </SectionTitle>

                      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                        {Object.entries(TRANSACTION_TYPES).map(([type, config]) => {
                          const selected = transactionType === type;
                          const isRcpt   = type === "Receipt";
                          return (
                            <Box
                              key={type}
                              onClick={() => {
                                setTransactionType(type);
                                setFieldErrors({});
                              }}
                              sx={{
                                border: "2px solid",
                                borderColor: selected
                                  ? (isRcpt ? "#059669" : "#dc2626")
                                  : "#e2e8f0",
                                borderRadius: "10px",
                                p: 2.5,
                                cursor: "pointer",
                                bgcolor: selected
                                  ? (isRcpt ? "#f0fdf4" : "#fef2f2")
                                  : "#fff",
                                transition: "all 0.15s ease",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 1,
                                "&:hover": {
                                  borderColor: isRcpt ? "#059669" : "#dc2626",
                                  bgcolor: isRcpt ? "#f0fdf4" : "#fef2f2",
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  color: selected
                                    ? (isRcpt ? "#059669" : "#dc2626")
                                    : "#9ca3af",
                                  "& svg": { fontSize: "2rem" },
                                }}
                              >
                                {config.icon}
                              </Box>
                              <Typography
                                sx={{
                                  fontWeight: 700, fontSize: "0.9375rem",
                                  color: selected
                                    ? (isRcpt ? "#059669" : "#dc2626")
                                    : "#374151",
                                }}
                              >
                                {type}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "0.75rem", color: "#6b7280",
                                  textAlign: "center", lineHeight: 1.4,
                                }}
                              >
                                {config.description}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </CardContent>
                  </SectionCard>

                  {/* Transaction Details */}
                  <SectionCard variant="outlined">
                    <CardContent sx={{ p: 3 }}>
                      <SectionTitle>
                        <InfoOutlinedIcon sx={{ fontSize: "1.125rem", color: "#3b82f6" }} />
                        Transaction Details
                      </SectionTitle>

                      {/* Row 1: Date | Reference */}
                      <FormRow>
                        <FormGroup
                          hasError={Boolean(fieldErrors.transactionDateTime)}
                          span={2}
                        >
                          <FieldLabel>
                            Transaction Date &amp; Time
                            <RequiredMark>*</RequiredMark>
                          </FieldLabel>
                          <TextField
                            type="datetime-local"
                            value={transactionDateTime}
                            onChange={(e) => {
                              setTransactionDateTime(e.target.value);
                              setFieldErrors((p) => ({ ...p, transactionDateTime: "" }));
                            }}
                            fullWidth size="small"
                            InputLabelProps={{ shrink: true }}
                            error={Boolean(fieldErrors.transactionDateTime)}
                            helperText={fieldErrors.transactionDateTime}
                            FormHelperTextProps={{ sx: helperErrorSx }}
                          />
                        </FormGroup>

                        <FormGroup span={2}>
                          <FieldLabel>
                            {isReceipt ? "PO / Invoice Number" : "Work Order / Job Number"}
                          </FieldLabel>
                          <TextField
                            value={referenceNumber}
                            onChange={(e) => setReferenceNumber(e.target.value)}
                            placeholder={isReceipt ? "e.g. PO-2024-001" : "e.g. WO-2024-055"}
                            fullWidth size="small"
                          />
                        </FormGroup>
                      </FormRow>

                      {/* Row 2: Qty | UOM | Unit Price */}
                      <FormRow>
                        <FormGroup hasError={Boolean(fieldErrors.qty)} span={1}>
                          <FieldLabel>
                            Quantity <RequiredMark>*</RequiredMark>
                          </FieldLabel>
                          <TextField
                            type="number"
                            value={qty}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === "" || Number(v) >= 0) setQty(v);
                              setFieldErrors((p) => ({ ...p, qty: "" }));
                            }}
                            placeholder="0"
                            inputProps={{ min: 0.001, step: "any" }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "#6b7280", fontWeight: 600 }}
                                  >
                                    {uom}
                                  </Typography>
                                </InputAdornment>
                              ),
                            }}
                            fullWidth size="small"
                            error={Boolean(fieldErrors.qty)}
                            helperText={
                              fieldErrors.qty ||
                              (transactionType === "Issue" && currentStock > 0
                                ? `Max: ${currentStock} ${uom}`
                                : "")
                            }
                            FormHelperTextProps={{
                              sx: fieldErrors.qty ? helperErrorSx : helperHintSx,
                            }}
                            sx={{
                              "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                                WebkitAppearance: "none",
                              },
                              "& input[type=number]": { MozAppearance: "textfield" },
                            }}
                          />
                        </FormGroup>

                        <FormGroup span={1}>
                          <FieldLabel>Unit of Measure</FieldLabel>
                          <Select
                            value={uom}
                            onChange={(e) => setUom(e.target.value)}
                            size="small" fullWidth
                            sx={{
                              background: "#fff",
                              "& fieldset": { borderColor: "#d1d5db" },
                              "&:hover fieldset": { borderColor: "#3b82f6" },
                            }}
                          >
                            {UOM_OPTIONS.map((u) => (
                              <MenuItem key={u} value={u}>{u}</MenuItem>
                            ))}
                          </Select>
                        </FormGroup>

                        <FormGroup span={2}>
                          <FieldLabel>Unit Price (₹)</FieldLabel>
                          <TextField
                            type="number"
                            value={unitPrice}
                            onChange={(e) => setUnitPrice(e.target.value)}
                            placeholder="0.00"
                            inputProps={{ min: 0, step: "0.01" }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Typography sx={{ color: "#6b7280", fontSize: "0.875rem" }}>
                                    ₹
                                  </Typography>
                                </InputAdornment>
                              ),
                            }}
                            fullWidth size="small"
                            sx={{
                              "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                                WebkitAppearance: "none",
                              },
                              "& input[type=number]": { MozAppearance: "textfield" },
                            }}
                          />
                        </FormGroup>
                      </FormRow>

                      {/* Row 3: Equipment (Issue only) — NOW SEARCHABLE */}
                      {transactionType === "Issue" && (
                        <FormRow>
                          <FormGroup
                            hasError={Boolean(fieldErrors.equipmentAssetId)}
                            span={4}
                          >
                            <FieldLabel>
                              Target Equipment / Asset
                              <RequiredMark>*</RequiredMark>
                            </FieldLabel>

                            {/* ← CHANGED: replaced plain Select with SearchableEquipmentSelect */}
                            <SearchableEquipmentSelect
                              value={equipmentAssetId}
                              onChange={(e) => {
                                setEquipmentAssetId(e.target.value);
                                setFieldErrors((p) => ({ ...p, equipmentAssetId: "" }));
                              }}
                              assets={equipmentAssets}
                              loading={loadingEquipment}
                              error={fieldErrors.equipmentAssetId}
                            />
                          </FormGroup>
                        </FormRow>
                      )}

                      {/* Row 4: Remarks */}
                      <FormRow>
                        <FormGroup span={4}>
                          <FieldLabel>Remarks</FieldLabel>
                          <TextField
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                            placeholder={
                              isReceipt
                                ? "e.g. Monthly replenishment from Vendor XYZ"
                                : "e.g. Issued for planned maintenance on Compressor A"
                            }
                            multiline minRows={2} maxRows={4}
                            fullWidth size="small"
                          />
                        </FormGroup>
                      </FormRow>
                    </CardContent>
                  </SectionCard>

                  {/* Transaction Summary Preview */}
                  {showQtyPreview && (
                    <SectionCard
                      variant="outlined"
                      sx={{ borderColor: accentColor, bgcolor: accentBg }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <SectionTitle
                          sx={{ color: accentColor, borderBottomColor: accentBorder }}
                        >
                          <InfoOutlinedIcon
                            sx={{ fontSize: "1.125rem", color: accentColor }}
                          />
                          Transaction Summary
                        </SectionTitle>

                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                            gap: 2,
                          }}
                        >
                          {[
                            {
                              label: "Type",
                              value: isReceipt ? "Stock In (Receipt)" : "Stock Out (Issue)",
                              color: accentColor,
                            },
                            { label: "Quantity",     value: `${qty} ${uom}` },
                            {
                              label: "Stock Before",
                              value: `${currentStock} ${uom}`,
                              color: "#6b7280",
                            },
                            {
                              label: "Stock After",
                              value: `${projectedStock} ${uom}`,
                              color: isReceipt
                                ? "#059669"
                                : projectedStock === 0 ? "#dc2626" : "#d97706",
                            },
                            ...(unitPrice
                              ? [{
                                  label: "Total Value",
                                  value: `₹${(Number(unitPrice) * Number(qty)).toLocaleString()}`,
                                  color: "#2563eb",
                                }]
                              : []),
                          ].map(({ label, value, color }) => (
                            <StatBox key={label}>
                              <StatLabel>{label}</StatLabel>
                              <Typography
                                sx={{
                                  fontSize: "0.9375rem",
                                  fontWeight: 700,
                                  color: color || "#111827",
                                }}
                              >
                                {value}
                              </Typography>
                            </StatBox>
                          ))}
                        </Box>
                      </CardContent>
                    </SectionCard>
                  )}
                </>
              )}

              {/* ══════════════════════════════════════════════════════════
                  TAB 1 — HISTORY
              ══════════════════════════════════════════════════════════ */}
              {activeTab === 1 && (
                <SectionCard variant="outlined">
                  <CardContent sx={{ p: 3 }}>
                    <SectionTitle>
                      <HistoryIcon sx={{ fontSize: "1.125rem", color: "#3b82f6" }} />
                      Transaction History
                      {loadingLogs && (
                        <CircularProgress
                          size={14}
                          sx={{ ml: 1, color: "#3b82f6" }}
                        />
                      )}
                    </SectionTitle>
                    <StockHistoryTable
                      logs={logs}
                      loading={loadingLogs}
                      openingStock={currentStock}
                    />
                  </CardContent>
                </SectionCard>
              )}

            </Stack>
          </Container>
        </Box>

        {/* ── Sticky footer ────────────────────────────────────────────── */}
        <Box
          sx={{
            borderTop: "1px solid #e2e8f0",
            backgroundColor: "#fff",
            py: 2, px: 3,
            boxShadow: "0 -2px 8px rgba(0,0,0,0.04)",
            flexShrink: 0,
          }}
        >
          <Container maxWidth={false} disableGutters>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
              <Button
                type="button" onClick={onClose}
                disabled={submitting} sx={cancelBtnSx}
              >
                Close
              </Button>

              {activeTab === 0 && (
                <Button
                  onClick={handleSubmit}
                  disabled={submitting || !qty || Number(qty) <= 0}
                  sx={{
                    px: 3, py: 1,
                    backgroundColor: accentColor,
                    color: "#fff",
                    borderRadius: "6px",
                    fontWeight: 600,
                    textTransform: "none",
                    minWidth: "180px",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    "&:hover": {
                      backgroundColor: isReceipt ? "#047857" : "#b91c1c",
                    },
                    "&:disabled": {
                      backgroundColor: isReceipt ? "#86efac" : "#fca5a5",
                      color: "#fff",
                    },
                  }}
                >
                  {submitting
                    ? <CircularProgress size={16} color="inherit" />
                    : typeConfig.icon}
                  {submitting ? "Processing…" : `Record ${transactionType}`}
                </Button>
              )}
            </Box>
          </Container>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ConsumableStockModal;